import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import { IDog, IDogStats } from "@eagerdog/interfaces";
import { Config } from "@eagerdog/config";
import { isDogProfileOwner, canOpenOwnDogProfile, DOG_OWNER_PREMIUM_ENABLED } from "@eagerdog/constants";

import { apiService } from "../../services/api.service";
import { helperService } from "../../services/helper.service";
import { localStorageService } from "../../services/local-storage.service";
import { toast } from "../../components/Toast/ToastManager";

import Button from "../../components/Button/Button";
import Icon from "../../components/Icon/Icon";
import TabContent, { ITab, useTabContent } from "../../components/TabContent/TabContent";
import DogPortrait from "../../components/DogPortrait/DogPortrait";
import RibbonRoomTab from "./RibbonRoomTab/RibbonRoomTab";
import StatsTab from "./StatsTab/StatsTab";
import PremiumGate from "./PremiumGate/PremiumGate";
import Feed from "../Discover/Feed/Feed";
import QuickActions from "../Discover/QuickActions/QuickActions";
import { Modal, useModal } from "../../components/Modal/Modal";
import ShareModal from "../../components/ShareModal/ShareModal";
import DogForm from "../../components/StepForm/DogForm/DogForm";
import Confirm from "../../components/Confirm/Confirm";
import ImageUploader from "../../components/ImageUploader/ImageUploader";

import "./DogProfile.scss";

interface IProps {}

const formatDob = (dob?: Date | string | null): string | null => {
  if (!dob) return null;
  const born = new Date(dob);
  if (isNaN(born.getTime())) return null;
  return born.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const formatLastCompeted = (d?: Date | string | null): string => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-US", sameYear
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" });
};

const DogProfile: React.FC<IProps> = (props) => {
  const { dogId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Only the My Dogs list sends us here with this state; links from the feed,
  // search, etc. land without a back link.
  const fromMyDogs = (location.state as { from?: string } | null)?.from === "my-dogs";

  // Land at the top when arriving from a scrolled page (feed, My Dogs).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [dogId]);
  const { setTab, activeTab } = useTabContent();
  const shareModal = useModal();
  const editModal = useModal();
  const makePublicConfirmModal = useModal();
  // Owner photo change: click the portrait → pick a file → crop → save.
  const photoModal = useModal();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [savingPhoto, setSavingPhoto] = useState(false);
  
  const user = helperService.getUser();
  const isLoggedIn = helperService.isLoggedIn();
  const [dog, setDog] = useState<IDog>();
  const [loading, setLoading] = useState<boolean>(true);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [followedDogs, setFollowedDogs] = useState<string[]>(user.followed_dogs || []);
  const [stats, setStats] = useState<IDogStats | null>(null);
  // Bumped after the owner posts from the Activity tab so the feed reloads.
  const [feedRefreshKey, setFeedRefreshKey] = useState(0);
  // Owners need the Dog Profile (Premium) feature for tracker data; public viewers always see it.
  // Dog Owner Premium is retired (DOG_OWNER_PREMIUM_ENABLED=false): tracker data is free for everyone.
  const trackerAccess = !isOwner || !DOG_OWNER_PREMIUM_ENABLED || canOpenOwnDogProfile(user);

  // Activity leads: it is the landing tab for shared links and visitors.
  const tabs: ITab[] = [
    { name: "Activity", actions: [] },
    { name: "Ribbon Room", actions: [] },
    { name: "Stats", actions: [] },
    { name: "Information", actions: [] }
  ];

  // Summary stats in the header share the Stats tab payload (fetched once here).
  useEffect(() => {
    if (!dog?._id || !trackerAccess) return;
    const fetchStats = isOwner ? apiService.getDogStats(dog._id) : apiService.getPublicDogStats(dog._id);
    fetchStats.then(setStats).catch(() => setStats(null));
  }, [dog?._id, isOwner, trackerAccess]);

  useEffect(() => {
    if (dogId) {
      // Try to load the dog - either as authenticated user or as public
      const loadDog = async () => {
        try {
          let dogData: IDog;
          
          if (isLoggedIn) {
            // If logged in, try the authenticated endpoint first
            try {
              dogData = await apiService.getDog(dogId);
              // Ownership / feature access use the shared helpers (same rules as mobile)
              const userIsOwner = isDogProfileOwner(user, dogData);
              setIsOwner(userIsOwner);

              // Owners without the Premium feature still get in; the Ribbon Room and
              // Stats tabs show an upsell instead of data (Information stays free).

              // If not owner and dog is not public, deny access
              if (!userIsOwner && !dogData.is_public) {
                toast.show({
                  title: "Dog Profile",
                  content: "You don't have permission to view this dog's profile",
                  duration: 10000,
                  type: "fail"
                });
                navigate("/account?t=0");
                return;
              }
            } catch (e: any) {
              // If authenticated call fails, try public endpoint
              dogData = await apiService.getPublicDog(dogId);
              setIsOwner(false);
            }
          } else {
            // Not logged in - use public endpoint
            dogData = await apiService.getPublicDog(dogId);
            setIsOwner(false);
          }
          
          setDog(dogData);
          setLoading(false);
        } catch (e: any) {
          toast.show({
            title: "Dog Profile",
            content: e.response?.status === 403 
              ? "This dog profile is private" 
              : "Unable to load dog information",
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });
          if (isLoggedIn) {
            navigate("/account?t=0");
          } else {
            navigate("/login?r=/dogs/" + dogId);
          }
        }
      };
      
      loadDog();
    }
  }, [dogId, isLoggedIn]);

  const onPhotoSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSelectedPhoto(file);
    if (!photoModal.isShown) photoModal.toggle();
  };

  const closePhotoModal = () => {
    setSelectedPhoto(null);
    if (photoModal.isShown) photoModal.toggle();
  };

  const savePhoto = async (file: File | null) => {
    if (!file || !dog || !dogId) return;
    setSavingPhoto(true);
    try {
      const uploaded: any = await apiService.uploadFile(file);
      const image_url = typeof uploaded === "string" ? uploaded : uploaded?.value;
      if (!image_url) throw new Error("Upload did not return a file URL");
      const updated = await apiService.updateDog(dogId, { image_url });
      setDog({ ...dog, image_url: updated.image_url || image_url });
      closePhotoModal();
    } catch {
      toast.show({
        title: "Dog photo",
        content: `Unable to update ${dog.call_name}'s photo. Please try again.`,
        duration: 8000,
        type: "fail",
      });
    } finally {
      setSavingPhoto(false);
    }
  };

  const togglePublicStatus = async () => {
    if (!dog || !dogId) return;

    const newPublicStatus = !dog.is_public;

    try {
      await apiService.updateDog(dogId, { is_public: newPublicStatus });
      
      setDog({ ...dog, is_public: newPublicStatus });
      
      toast.show({
        title: newPublicStatus ? "Profile is now Public" : "Profile is now Private",
        content: newPublicStatus 
          ? "Others can now view your dog's profile when you share the link" 
          : "Your dog's profile is now private",
        duration: 5000,
        type: "success"
      });
    } catch (e: any) {
      toast.show({
        title: "Error",
        content: "Failed to update profile visibility",
        duration: 5000,
        errorDetails: e,
        type: "fail"
      });
    }
  };

  const isFollowing = !!dogId && followedDogs.some((id) => id === dogId);

  const toggleFollow = () => {
    if (!dogId) return;
    if (!isLoggedIn) {
      navigate(`/login?r=/dogs/${dogId}`);
      return;
    }

    const previous = followedDogs;
    const next = isFollowing
      ? previous.filter((id) => id !== dogId)
      : [...previous, dogId];
    // Header count tracks the viewer's own toggle; rolled back if the save fails.
    const delta = isFollowing ? -1 : 1;
    const bumpFollowers = (by: number) =>
      setDog((d) => d ? { ...d, follower_count: Math.max(0, (d.follower_count || 0) + by) } : d);
    setFollowedDogs(next);
    bumpFollowers(delta);
    apiService.updateSelf({ followed_dogs: next }).then((updatedUser) => {
      localStorageService.setItem(localStorageService.USER, JSON.stringify(updatedUser));
      setFollowedDogs(updatedUser.followed_dogs || next);
    }).catch((e: any) => {
      setFollowedDogs(previous);
      bumpFollowers(-delta);
      toast.show({
        title: "Follow",
        content: e.response?.data?.message || "Unable to update who you follow",
        duration: 5000,
        type: "fail",
      });
    });
  };

  const handleShareClick = () => {
    if (!dog?.is_public) {
      // Show confirmation modal to make public first
      makePublicConfirmModal.toggle();
    } else {
      // Profile is already public, show share modal
      shareModal.toggle();
    }
  };

  const handleMakePublicAndShare = async () => {
    makePublicConfirmModal.toggle();
    
    if (!dog || !dogId) return;

    try {
      await apiService.updateDog(dogId, { is_public: true });
      setDog({ ...dog, is_public: true });
      
      toast.show({
        title: "Profile is now Public",
        content: "Others can now view your dog's profile",
        duration: 5000,
        type: "success"
      });
      
      // Now show the share modal
      shareModal.toggle();
    } catch (e: any) {
      toast.show({
        title: "Error",
        content: "Failed to make profile public",
        duration: 5000,
        errorDetails: e,
        type: "fail"
      });
    }
  };

  const renderInformation = () => {
    if (!dog) return null;
    
    return (
      <div className="dogInfoTab">
        <div className="tabIntro">
          <h2 className="tabTitle">About {dog.call_name}</h2>
          <p className="tabDescription">Registration details, breed, and club memberships.</p>
        </div>

        <div className="infoSection">
          <h3>Basic Information</h3>
          <div className="infoGrid">
            <div className="infoItem">
              <span className="label">Call Name</span>
              <span className="value">{dog.call_name}</span>
            </div>
            {dog.registered_name && (
              <div className="infoItem">
                <span className="label">Registered Name</span>
                <span className="value">
                  {dog.prefix_title && `${dog.prefix_title} `}
                  {dog.registered_name}
                  {dog.suffix_title && ` ${dog.suffix_title}`}
                </span>
              </div>
            )}
            <div className="infoItem">
              <span className="label">Breed</span>
              <span className="value">{dog.breed}{dog.variety && ` - ${dog.variety}`}</span>
            </div>
            <div className="infoItem">
              <span className="label">Sex</span>
              <span className="value">{dog.sex}</span>
            </div>
            <div className="infoItem">
              <span className="label">Date of Birth</span>
              <span className="value">{dog.dob ? new Date(dog.dob).toLocaleDateString() : "—"}</span>
            </div>
            {dog.height_at_withers && (
              <div className="infoItem">
                <span className="label">Height at Withers</span>
                <span className="value">{dog.height_at_withers}"</span>
              </div>
            )}
          </div>
        </div>

        {dog.club_registrations && dog.club_registrations.length > 0 && (
          <div className="infoSection">
            <h3>Registered Clubs</h3>
            <div className="clubTags">
              {dog.club_registrations.map((reg, idx) => (
                <span key={idx} className="clubTag">{reg.sanctioning_club}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading || !dog) {
    return (
      <div className="dogProfilePage">
        <div className="container">
          <div className="loading">Loading dog profile...</div>
        </div>
      </div>
    );
  }

  // Build SEO-friendly title and description
  const fullName = dog.registered_name 
    ? `${dog.prefix_title ? dog.prefix_title + ' ' : ''}${dog.registered_name}${dog.suffix_title ? ' ' + dog.suffix_title : ''}`
    : dog.call_name;
  
  const pageTitle = `${dog.call_name} - ${dog.breed} | Eager Dog`;
  const pageDescription = dog.registered_name
    ? `Meet ${dog.call_name} (${fullName}), a talented ${dog.breed}${dog.variety ? ' - ' + dog.variety : ''} competing in dog sports.`
    : `Meet ${dog.call_name}, a talented ${dog.breed}${dog.variety ? ' - ' + dog.variety : ''} competing in dog sports.`;
  
  // The dog's own URL is what gets shared; the web edge hands crawlers the
  // /preview page for it, so the link unfurls without exposing /preview.
  const pageUrl = `${(Config.web.REACT_APP_WEB_URL || window.location.origin).replace(/\/$/, "")}/dogs/${dogId}`;
  const apiBase = Config.web.REACT_APP_API_URL || window.location.origin;
  const ogImageUrl = `${apiBase}/preview/dog/${dogId}/og-image.png`;

  const breedLine = `${dog.breed}${dog.variety ? ` - ${dog.variety}` : ""}`;
  const sexDobLine = [dog.sex, formatDob(dog.dob)].filter(Boolean).join(" - ");

  const summaryStats: Array<[string, string]> = [
    ["Titles earned", stats ? String(stats.titles_earned) : "—"],
    ["Qualifying runs", stats ? String(stats.results.qualified) : "—"],
    ["Sports", stats ? String(stats.by_sport.length) : "—"],
    ["Last competed", stats ? formatLastCompeted(stats.last_run_date) : "—"],
  ];

  return (
    <div className="dogProfilePage">
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={fullName || dog.call_name} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${dog.call_name} - ${dog.breed}`} />
        <meta property="og:site_name" content="Eager Dog" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={fullName || dog.call_name} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        <meta name="twitter:image:alt" content={`${dog.call_name} - ${dog.breed}`} />
        
        {/* Additional Meta Tags */}
        <link rel="canonical" href={pageUrl} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="container">
        {isOwner && fromMyDogs && (
          <Link to="/account?t=0" className="backLink">Back to My Dogs</Link>
        )}

        <section className="dogProfileHeader">
          <div className="headerMain">
            {isOwner ? (
              <button
                type="button"
                className="dogImageWrapper dogImageButton"
                onClick={() => photoInputRef.current?.click()}
                aria-label={`Change ${dog.call_name}'s photo`}
                title="Change photo"
              >
                <DogPortrait dog={dog} showName={false} className="size-profile" />
                <span className="dogImageOverlay">Change photo</span>
              </button>
            ) : (
              <div className="dogImageWrapper">
                <DogPortrait dog={dog} showName={false} className="size-profile" />
              </div>
            )}
            <div className="dogHeaderInfo">
              <div className="kickerRow">
                <p className="kicker">Dog profile</p>
                {isOwner && (
                  <span className="visibilityBadge">{dog.is_public ? "Public" : "Private"}</span>
                )}
              </div>
              <h1>{dog.call_name}</h1>
              {dog.registered_name && (
                <p className="registeredName">
                  {dog.prefix_title && `${dog.prefix_title} `}
                  {dog.registered_name}
                  {dog.suffix_title && ` ${dog.suffix_title}`}
                </p>
              )}
              <p className="dogBreed">{breedLine}</p>
              {sexDobLine && <p className="dogBreed">{sexDobLine}</p>}
              <div
                className="followersInline"
                aria-live="polite"
                title={`${(dog.follower_count || 0).toLocaleString()} ${dog.follower_count === 1 ? "follower" : "followers"}`}
              >
                <Icon type="group" className="followersIcon" />
                <strong>{helperService.formatCompactNumber(dog.follower_count || 0)}</strong>
                <span>{dog.follower_count === 1 ? "follower" : "followers"}</span>
              </div>
            </div>
            {(isOwner || dog.is_public) && (
              <div className="actions">
                {!isOwner && (
                  <Button
                    className="followBtn"
                    variant={isFollowing ? "secondary" : "default"}
                    onClick={toggleFollow}
                  >
                    {isFollowing ? `Following ${dog.call_name}` : `Follow ${dog.call_name}`}
                  </Button>
                )}
                {isOwner && (
                  <div className="circleButtons">
                    <div className="btnWrap">
                      <button type="button" onClick={editModal.toggle} className="edit" title="Edit Dog">Edit Dog</button>
                    </div>
                    <div className="btnWrap">
                      <button
                        type="button"
                        onClick={togglePublicStatus}
                        className={dog.is_public ? "visibility public" : "visibility private"}
                        title={dog.is_public ? "Profile is public — make private" : "Profile is private — make public"}
                      >
                        {dog.is_public ? "Make Private" : "Make Public"}
                      </button>
                    </div>
                    <div className="btnWrap">
                      <button type="button" onClick={handleShareClick} className="share" title="Share Profile">Share Profile</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="summaryStats">
            {summaryStats.map(([label, value]) => (
              <div key={label} className="stat">
                <p className="statValue">{value}</p>
                <p className="statLabel">{label}</p>
              </div>
            ))}
          </div>

          <nav className="profileTabs" aria-label="Dog profile sections">
            {tabs.map((tab, index) => (
              <button
                key={tab.name}
                type="button"
                className={index === activeTab ? "tab active" : "tab"}
                onClick={() => setTab(index)}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </section>

        <div className="dogProfileContent">
          <TabContent setTab={setTab} activeTab={activeTab} tabs={tabs} hideTabs={true}>
            <div className="dogActivityTab">
              {isOwner && <QuickActions dog={dog} onPosted={() => setFeedRefreshKey((k) => k + 1)} />}
              <Feed key={feedRefreshKey} source={{ kind: "dog", dogId: dog._id, isOwner, dogName: dog.call_name }} />
            </div>
            {trackerAccess ? (
              <RibbonRoomTab dog={dog} isOwner={isOwner} />
            ) : (
              <PremiumGate
                dogName={dog.call_name}
                feature="Ribbon Room"
                bullets={[
                  "Track titles and legs across every sport",
                  "See exactly what's needed for the next title",
                  "Add past runs from other clubs and events",
                ]}
              />
            )}
            {trackerAccess ? (
              <StatsTab dog={dog} isOwner={isOwner} preloadedStats={stats} />
            ) : (
              <PremiumGate
                dogName={dog.call_name}
                feature="Stats"
                bullets={[
                  "Lifetime events, runs, and Q rate",
                  "Personal bests and Q streaks",
                  "Entry fees paid, per run and per Q",
                ]}
              />
            )}
            {renderInformation()}
          </TabContent>
        </div>
      </div>

      {/* Share Modal */}
      {shareModal.isShown && (
        <Modal
          isShown={shareModal.isShown}
          hide={shareModal.toggle}
          className="ShareModalContainer"
          modalContent={
            <ShareModal
              eventTitle={fullName || dog.call_name}
              eventUrl={pageUrl}
              shareType="dog"
              onClose={shareModal.toggle}
            />
          }
        />
      )}

      {/* Edit Dog Modal */}
      <Modal
        className="dogForm"
        isShown={editModal.isShown}
        hide={editModal.toggle}
        modalContent={
          <DogForm 
            onFinish={(updatedDog) => { 
              if (updatedDog) {
                setDog(updatedDog);
              }
              editModal.toggle();
            }}
            onDogUpdated={(updatedDog) => setDog(updatedDog)}
            dog={dog} 
          />
        }
      />

      {/* Dog photo: hidden picker + crop modal (owner only) */}
      {isOwner && (
        <>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            className="dogPhotoInput"
            onChange={onPhotoSelected}
          />
          <Modal
            className="profilePhotoModal"
            isShown={photoModal.isShown}
            hide={closePhotoModal}
            modalContent={
              <div className="dogPhotoModal">
                {selectedPhoto && (
                  <ImageUploader
                    label={`${dog.call_name}'s photo`}
                    initialFile={selectedPhoto}
                    aspectRatio={1}
                    circularCrop
                    maxWidth={600}
                    maxHeight={600}
                    onImageChange={savePhoto}
                    onCancel={closePhotoModal}
                  />
                )}
                {savingPhoto && <div className="saving">Saving photo…</div>}
              </div>
            }
          />
        </>
      )}

      {/* Make Public Confirmation Modal */}
      <Modal
        className="Confirmation"
        isShown={makePublicConfirmModal.isShown}
        hide={makePublicConfirmModal.toggle}
        modalContent={
          <Confirm
            message={
              <>
                <p>In order for others to see your dog's page, you need to make it public.</p>
                <p>Would you like to do that now?</p>
              </>
            }
            onConfirm={handleMakePublicAndShare}
            onCancel={makePublicConfirmModal.toggle}
          />
        }
      />
    </div>
  );
};

export default DogProfile;

